import { useEffect, useRef } from "react";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "sonner";

/**
 * Hook to monitor token expiration and show warnings
 */
export function useTokenExpiration() {
  const { session } = useAuthStore();
  const warningShownRef = useRef(false);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any existing interval
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
    }

    // Reset warning flag when session changes
    warningShownRef.current = false;

    if (!session?.expires_at) {
      return;
    }

    const checkExpiration = () => {
      if (!session?.expires_at) return;

      const now = Math.floor(Date.now() / 1000);
      const expiresAt = session.expires_at;
      const timeUntilExpiry = expiresAt - now;

      // Show warning if less than 5 minutes remaining and not already shown
      const fiveMinutes = 5 * 60;
      if (
        timeUntilExpiry < fiveMinutes &&
        timeUntilExpiry > 0 &&
        !warningShownRef.current
      ) {
        const minutesLeft = Math.ceil(timeUntilExpiry / 60);

        toast.warning(
          `Your session will expire in ${minutesLeft} minute${
            minutesLeft !== 1 ? "s" : ""
          }`,
          {
            position: "top-center",
            duration: 10000,
            description: "Your session will be automatically refreshed.",
          }
        );

        warningShownRef.current = true;
      }

      // If token has expired, the apiClient will handle refresh automatically
    };

    // Check immediately
    checkExpiration();

    // Then check every minute
    checkIntervalRef.current = setInterval(checkExpiration, 60 * 1000);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [session?.expires_at]);
}
